import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import SignatureRequest from '../../components/SignatureRequest';
import { services } from '../../utils/demoData';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { db, storage } from '../../services/firebase';
import { collection, addDoc, getDocs, query, where, orderBy, doc, updateDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, listAll } from 'firebase/storage';
import { generateSignedPDF, uploadPDFToStorage } from '../../utils/pdfGenerator';
import { getAppointmentColors } from '../../utils/appointmentColors';

const Estimates = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { createEstimateNotification, getAdminUserIds } = useNotifications();
  const [estimates, setEstimates] = useState([]);
  const [showNewEstimate, setShowNewEstimate] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingEstimates, setLoadingEstimates] = useState(true);
  const [notification, setNotification] = useState(null);
  const [errors, setErrors] = useState({});
  
  // Edit/View states
  const [editingEstimate, setEditingEstimate] = useState(null);
  const [viewingEstimate, setViewingEstimate] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  
  // Signature states
  const [submittingSignature, setSubmittingSignature] = useState(false);
  
  // Delete confirmation states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [estimateToDelete, setEstimateToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Message states
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    if (currentUser) {
      loadEstimates();
    } else {
      // If no user, set loading to false and empty estimates
      setLoadingEstimates(false);
    setEstimates([]);
    }
  }, [currentUser]);

  // Calculate unread admin messages whenever estimates change


  // Function to check and fix estimates with missing file references
  const checkAndFixEstimateFiles = async (estimate) => {
    if (!estimate.id || !currentUser) return estimate;
    
    try {
      // Check if estimate already has files
      if (estimate.files && estimate.files.length > 0) {
        console.log(`Estimate ${estimate.id} already has ${estimate.files.length} files`);
        return estimate;
      }
      
      // Check Firebase Storage for files
      const storageRef = ref(storage, `estimates/${currentUser.uid}/${estimate.id}`);
      const listResult = await listAll(storageRef);
      
      if (listResult.items.length > 0) {
        console.log(`Found ${listResult.items.length} orphaned files for estimate ${estimate.id}`);
        
        // Get file details and download URLs
        const filePromises = listResult.items.map(async (item) => {
          try {
            const downloadURL = await getDownloadURL(item);
            const fileName = item.name;
            
            // Try to determine file type from name
            const fileExtension = fileName.split('.').pop().toLowerCase();
            let fileType = 'application/octet-stream';
            if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension)) {
              fileType = `image/${fileExtension === 'jpg' ? 'jpeg' : fileExtension}`;
            } else if (['mp4', 'webm', 'avi', 'mov'].includes(fileExtension)) {
              fileType = `video/${fileExtension}`;
            }
            
            return {
              name: fileName,
              url: downloadURL,
              size: 0, // We can't get size from storage reference easily
              type: fileType,
              storagePath: item.fullPath
            };
          } catch (error) {
            console.error('Error getting file details:', error);
            return null;
          }
        });
        
        const files = (await Promise.all(filePromises)).filter(f => f !== null);
        
        if (files.length > 0) {
          console.log('Updating estimate with recovered files:', files);
          
          // Update Firestore document
          await updateDoc(doc(db, 'estimates', estimate.id), {
            files: files,
            updatedAt: new Date()
          });
          
          // Return updated estimate
          return {
            ...estimate,
            files: files
          };
        }
      }
    } catch (error) {
      console.error('Error checking/fixing estimate files:', error);
    }
    
    return estimate;
  };

  const loadEstimates = async () => {
    if (!currentUser) {
      setLoadingEstimates(false);
      return;
    }
    
    setLoadingEstimates(true);
    try {
      console.log('Loading estimates for user:', currentUser.uid);
      console.log('Firebase config check:', {
        hasDb: !!db,
        hasAuth: !!currentUser,
        userUid: currentUser.uid
      });
      
      const estimatesRef = collection(db, 'estimates');
      
      // Try a simple query first to check if collection exists
      let querySnapshot;
      try {
        // First try with orderBy
        const q = query(
          estimatesRef, 
          where('userId', '==', currentUser.uid),
          orderBy('createdAt', 'desc')
        );
        querySnapshot = await getDocs(q);
        console.log('Query with orderBy successful');
      } catch (orderError) {
        console.log('OrderBy failed, trying without orderBy:', orderError.message);
        try {
          // If orderBy fails (no index), try without it
          const q = query(
            estimatesRef, 
            where('userId', '==', currentUser.uid)
          );
          querySnapshot = await getDocs(q);
          console.log('Query without orderBy successful');
        } catch (whereError) {
          console.log('Where query failed, trying simple collection query:', whereError.message);
          // If where also fails, try just getting the collection
          querySnapshot = await getDocs(estimatesRef);
          console.log('Simple collection query successful');
        }
      }
      
      const estimatesList = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Estimate document:', doc.id, data);
        console.log('Files in estimate:', data.files);
        
        // Filter by userId if we used the simple collection query
        if (data.userId === currentUser.uid || !data.userId) {
          estimatesList.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
            submittedDate: data.createdAt?.toDate ? 
              data.createdAt.toDate().toISOString().split('T')[0] : 
              (data.createdAt ? new Date(data.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0])
          });
        }
      });
      
      // Sort manually if we couldn't use orderBy
      estimatesList.sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return dateB - dateA;
      });
      
      // Check and fix any estimates with missing file references
      console.log('Checking for orphaned files in estimates...');
      const fixedEstimates = await Promise.all(
        estimatesList.map(estimate => checkAndFixEstimateFiles(estimate))
      );
      
      setEstimates(fixedEstimates);
      console.log('Successfully loaded estimates:', fixedEstimates.length);
      
      if (fixedEstimates.length === 0) {
        console.log('No estimates found for user');
      }
      
    } catch (error) {
      console.error('Error loading estimates:', error);
      
      // More specific error handling
      if (error.code === 'permission-denied') {
        showNotification('Permission denied. Please make sure you are logged in and try again.', 'error');
      } else if (error.code === 'failed-precondition') {
        showNotification('Database index required. Estimates will load after first submission.', 'error');
      } else if (error.code === 'unavailable') {
        showNotification('Database temporarily unavailable. Please try again in a moment.', 'error');
      } else {
        console.log('Using fallback: setting empty estimates array');
        setEstimates([]); // Set empty array instead of showing error for new users
      }
    } finally {
      setLoadingEstimates(false);
    }
  };

  // Cleanup object URLs when component unmounts or files change
  useEffect(() => {
    return () => {
      // Clean up any remaining object URLs
      files.forEach(file => {
        if (file instanceof File) {
          const url = URL.createObjectURL(file);
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [files]);

  // Auto-hide notifications after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!selectedService.trim()) {
      newErrors.service = 'Please select a service type';
    }
    
    if (!description.trim()) {
      newErrors.description = 'Please provide a description';
    } else if (description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const maxFileSize = 10 * 1024 * 1024; // 10MB in bytes
    const maxFiles = 5; // Maximum 5 files
    
    // Check file count limit
    if (selectedFiles.length > maxFiles) {
      showNotification(`You can only upload up to ${maxFiles} files at once.`, 'error');
      return;
    }
    
    // Filter out files that are too large
    const validFiles = [];
    const invalidFiles = [];
    
    selectedFiles.forEach(file => {
      if (file.size > maxFileSize) {
        invalidFiles.push(file.name);
      } else {
        validFiles.push(file);
      }
    });
    
    if (invalidFiles.length > 0) {
      showNotification(
        `${invalidFiles.length} file(s) were too large (max 10MB): ${invalidFiles.join(', ')}`, 
        'error'
      );
    }
    
    if (validFiles.length > 0) {
      setFiles(prevFiles => [...prevFiles, ...validFiles]);
      showNotification(`${validFiles.length} file(s) added successfully!`, 'success');
    }
    
    // Clear the input so the same files can be selected again if needed
    e.target.value = '';
  };

  const removeFile = (indexToRemove) => {
    setFiles(files.filter((_, index) => index !== indexToRemove));
  };

  const isImageFile = (file) => {
    return file.type.startsWith('image/');
  };

  const isVideoFile = (file) => {
    return file.type.startsWith('video/');
  };

  const getFilePreviewUrl = (file) => {
    return URL.createObjectURL(file);
  };

  const uploadFilesToStorage = async (files, estimateId) => {
    if (!files || files.length === 0) {
      console.log('No files to upload');
      return [];
    }
    
    console.log('Starting file upload for', files.length, 'files to estimate:', estimateId);
    
    const uploadPromises = files.map(async (file, index) => {
      try {
        console.log(`Uploading file ${index + 1}/${files.length}:`, file.name, 'Size:', file.size, 'Type:', file.type);
        
        // Create a unique filename
        const timestamp = Date.now();
        const fileExtension = file.name.split('.').pop();
        const fileName = `${timestamp}_${index}.${fileExtension}`;
        
        // Create storage reference
        const storagePath = `estimates/${currentUser.uid}/${estimateId}/${fileName}`;
        const storageRef = ref(storage, storagePath);
        
        console.log('Storage path:', storagePath);
        
        // Upload file
        const snapshot = await uploadBytes(storageRef, file);
        console.log('File uploaded to storage:', snapshot.ref.fullPath);
        
        // Get download URL
        const downloadURL = await getDownloadURL(snapshot.ref);
        console.log('Download URL obtained:', downloadURL);
        
        const fileData = {
          name: file.name,
          url: downloadURL,
          size: file.size,
          type: file.type,
          storagePath: snapshot.ref.fullPath
        };
        
        console.log('File data prepared:', fileData);
        return fileData;
      } catch (error) {
        console.error('Error uploading file:', file.name, error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          stack: error.stack
        });
        throw error;
      }
    });
    
    try {
      const results = await Promise.all(uploadPromises);
      console.log('All files uploaded successfully:', results);
      return results;
    } catch (error) {
      console.error('Error in Promise.all for file uploads:', error);
      throw error;
    }
  };

  const handleSubmitEstimate = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});
    
    // Validate form
    if (!validateForm()) {
      showNotification('Please fix the errors below and try again.', 'error');
      return;
    }

    if (!currentUser) {
      showNotification('You must be logged in to submit an estimate request.', 'error');
      return;
    }

    setLoading(true);
    
    try {
      // First, create the estimate document in Firestore
      const estimateData = {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: currentUser.displayName || currentUser.email.split('@')[0],
      service: selectedService,
        description: description.trim(),
      status: 'pending',
        createdAt: new Date(),
        updatedAt: new Date(),
        files: [], // Will be updated after file upload
      messages: []
    };

      // Add estimate to Firestore
      const docRef = await addDoc(collection(db, 'estimates'), estimateData);
      console.log('Estimate document created with ID:', docRef.id);

      // Upload files to Firebase Storage if any
      let uploadedFiles = [];
      if (files.length > 0) {
        console.log('Files to upload:', files.length);
        showNotification('Uploading files...', 'success');
        try {
          uploadedFiles = await uploadFilesToStorage(files, docRef.id);
          console.log('Files uploaded, updating Firestore document with:', uploadedFiles);
          
          // Create initial messages from user's description and files
          const initialMessages = [];
          
          // Add description as initial message
          if (description.trim()) {
            initialMessages.push({
              id: Date.now().toString(),
              text: description.trim(),
              sender: 'client',
              senderName: currentUser.displayName || currentUser.email.split('@')[0],
              senderEmail: currentUser.email,
              timestamp: new Date(),
              read: false,
              isInitialRequest: true
            });
          }

          // Add each uploaded file as a separate message
          if (uploadedFiles.length > 0) {
            uploadedFiles.forEach((file, index) => {
              initialMessages.push({
                id: (Date.now() + index + 1).toString(),
                text: '', // No caption for initial files
                sender: 'client',
                senderName: currentUser.displayName || currentUser.email.split('@')[0],
                senderEmail: currentUser.email,
                timestamp: new Date(),
                read: false,
                fileUrl: file.url,
                fileName: file.name,
                fileType: file.type,
                fileSize: file.size,
                isInitialRequest: true
              });
            });
          }

          // Update the estimate document with file information and initial messages
          await updateDoc(doc(db, 'estimates', docRef.id), {
            files: uploadedFiles,
            messages: initialMessages,
            hasUnreadMessages: initialMessages.length > 0,
            updatedAt: new Date()
          });
          
          console.log('Firestore document updated with files successfully');
        } catch (fileError) {
          console.error('File upload had issues but estimate was saved:', fileError);
          // Don't fail the whole process if files fail - estimate is already saved
          showNotification('Estimate saved! Some files may not have uploaded properly.', 'error');
        }
      } else {
        console.log('No files selected for upload');
        
        // Still create initial message from description if no files
        const initialMessages = [];
        if (description.trim()) {
          initialMessages.push({
            id: Date.now().toString(),
            text: description.trim(),
            sender: 'client',
            senderName: currentUser.displayName || currentUser.email.split('@')[0],
            senderEmail: currentUser.email,
            timestamp: new Date(),
            read: false,
            isInitialRequest: true
          });

          // Update the estimate document with initial message
          await updateDoc(doc(db, 'estimates', docRef.id), {
            messages: initialMessages,
            hasUnreadMessages: true,
            updatedAt: new Date()
          });
        }
      }

      // Create the new estimate object for local state
      const newEstimate = {
        id: docRef.id,
        ...estimateData,
        files: uploadedFiles,
        submittedDate: new Date().toISOString().split('T')[0]
      };

      // Update local state
    setEstimates([newEstimate, ...estimates]);
    
    // Reset form
    setSelectedService('');
    setDescription('');
    setFiles([]);
      setErrors({});
    setShowNewEstimate(false);
      
      // Notify admins about new estimate
      try {
        const adminUserIds = await getAdminUserIds();
        for (const adminId of adminUserIds) {
          await createEstimateNotification(newEstimate, 'estimate_submitted', adminId);
        }
      } catch (notificationError) {
        console.error('Error sending notifications to admins:', notificationError);
      }
      
      showNotification(
        `🎉 Estimate request submitted successfully! ${files.length > 0 ? `${files.length} file(s) uploaded. ` : ''}We'll review it and get back to you soon.`,
        'success'
      );
      
    } catch (error) {
      console.error('Error submitting estimate:', error);
      
      // Provide more specific error messages
      if (error.code === 'permission-denied') {
        showNotification('Permission denied. Please make sure you are logged in and try again.', 'error');
      } else if (error.code === 'storage/unauthorized') {
        showNotification('File upload failed due to permissions. Please try again.', 'error');
      } else {
        showNotification('Failed to submit estimate request. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ff9800';
      case 'quoted': return '#2196f3';
      case 'approved': return '#4caf50';
      case 'declined': return '#f44336';
      default: return '#666';
    }
  };

  const handleDeleteEstimate = (estimateId) => {
    const estimate = estimates.find(est => est.id === estimateId);
    setEstimateToDelete(estimate);
    setShowDeleteModal(true);
  };

  const confirmDeleteEstimate = async () => {
    if (!estimateToDelete) return;

    setDeleting(true);
    try {
      // Delete from Firestore
      await deleteDoc(doc(db, 'estimates', estimateToDelete.id));
      
      // Update local state
      setEstimates(estimates.filter(est => est.id !== estimateToDelete.id));
      
      showNotification('Estimate request deleted successfully.', 'success');
      
      // Close modal
      setShowDeleteModal(false);
      setEstimateToDelete(null);
    } catch (error) {
      console.error('Error deleting estimate:', error);
      showNotification('Failed to delete estimate request. Please try again.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const cancelDeleteEstimate = () => {
    setShowDeleteModal(false);
    setEstimateToDelete(null);
  };

  const canEditOrDelete = (status) => {
    return status === 'pending';
  };

  // View estimate details
  const handleViewEstimate = (estimate) => {
    console.log('Viewing estimate:', estimate);
    console.log('Estimate files:', estimate.files);
    setViewingEstimate(estimate);
    setShowViewModal(true);
  };

  // Edit estimate
  const handleEditEstimate = (estimate) => {
    console.log('Editing estimate:', estimate);
    console.log('Estimate files:', estimate.files);
    setEditingEstimate(estimate);
    setSelectedService(estimate.service);
    setDescription(estimate.description);
    setFiles([]); // Reset files for editing
    setShowEditModal(true);
  };

  // Update estimate
  const handleUpdateEstimate = async (e) => {
    e.preventDefault();
    
    if (!editingEstimate) return;
    
    // Clear previous errors
    setErrors({});
    
    // Validate form
    if (!validateForm()) {
      showNotification('Please fix the errors below and try again.', 'error');
      return;
    }

    setLoading(true);
    
    try {
      // Update estimate data
      const updateData = {
        service: selectedService,
        description: description.trim(),
        updatedAt: new Date()
      };

      // Upload new files if any
      let uploadedFiles = editingEstimate.files || [];
      if (files.length > 0) {
        showNotification('Uploading new files...', 'success');
        try {
          const newUploadedFiles = await uploadFilesToStorage(files, editingEstimate.id);
          uploadedFiles = [...uploadedFiles, ...newUploadedFiles];
          updateData.files = uploadedFiles;
        } catch (fileError) {
          console.log('File upload had issues but estimate will be updated:', fileError);
          showNotification('Estimate updated! Some files may not have uploaded properly.', 'success');
        }
      }

      // Update in Firestore
      await updateDoc(doc(db, 'estimates', editingEstimate.id), updateData);

      // Update local state
      const updatedEstimates = estimates.map(est => 
        est.id === editingEstimate.id 
          ? { ...est, ...updateData, files: uploadedFiles }
          : est
      );
      setEstimates(updatedEstimates);
      
      // Reset form and close modal
      setSelectedService('');
      setDescription('');
      setFiles([]);
      setErrors({});
      setEditingEstimate(null);
      setShowEditModal(false);
      
      showNotification('🎉 Estimate updated successfully!', 'success');
      
    } catch (error) {
      console.error('Error updating estimate:', error);
      showNotification('Failed to update estimate. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Chat functions removed

  // Handle signature submission
  const handleSignatureSubmit = async (signatureData, signatureMessage) => {
    console.log('handleSignatureSubmit called with:', { signatureData: !!signatureData, signatureMessage: !!signatureMessage, viewingEstimate: !!viewingEstimate });
    
    if (!signatureData || !signatureMessage || !viewingEstimate) {
      console.error('Missing required data for signature submission');
      showNotification('Invalid signature data.', 'error');
      return;
    }

    setSubmittingSignature(true);
    try {
      console.log('Starting signature submission process...');
      
      // Generate signed PDF with signature
      console.log('Generating signed PDF...');
      const signedPdfBlob = await generateSignedPDF(
        viewingEstimate,
        { 
          amount: viewingEstimate.quoteAmount, 
          notes: viewingEstimate.quoteNotes 
        },
        signatureData
      );
      console.log('Signed PDF generated, blob size:', signedPdfBlob.size);
      
      // Upload signed PDF to Firebase Storage
      const signedPdfPath = `signatures/${viewingEstimate.userId}/${viewingEstimate.id}/signed_estimate_${Date.now()}.pdf`;
      console.log('Uploading signed PDF to:', signedPdfPath);
      const signedPdfUrl = await uploadPDFToStorage(signedPdfBlob, signedPdfPath);
      console.log('Signed PDF uploaded successfully, URL:', signedPdfUrl);
      
      // Create signature completion message
      const completionMessage = {
        id: Date.now().toString(),
        text: 'Document signed successfully! Thank you for your signature.',
        sender: 'system',
        senderName: 'System',
        senderEmail: 'system@greenoaktree.com',
        timestamp: new Date(),
        read: false,
        isSignatureCompletion: true,
        signatureData: signatureData,
        signedPdfUrl: signedPdfUrl,
        originalSignatureRequestId: signatureMessage.signatureRequestId,
        estimateData: {
          id: viewingEstimate.id,
          service: viewingEstimate.service,
          userName: viewingEstimate.userName,
          userEmail: viewingEstimate.userEmail,
          quoteAmount: viewingEstimate.quoteAmount,
          signedAt: new Date()
        }
      };

      // Update the original signature request message to mark as completed
      const currentMessages = viewingEstimate.messages || [];
      const updatedMessages = currentMessages.map(msg => {
        if (msg.signatureRequestId === signatureMessage.signatureRequestId) {
          return {
            ...msg,
            signatureStatus: 'completed',
            signedAt: new Date(),
            signatureData: signatureData,
            signedPdfUrl: signedPdfUrl,
            estimateData: {
              ...viewingEstimate,
              quoteAmount: viewingEstimate.quoteAmount || null,
              quoteNotes: (() => {
                // Preserve the quote notes from the original signature request
                if (msg.estimateData && msg.estimateData.quoteNotes) {
                  return msg.estimateData.quoteNotes;
                }
                // Fallback to finding the latest quote message
                const messages = viewingEstimate.messages || [];
                const quoteMessages = messages.filter(m => m.isQuote && m.quoteNotes);
                const latestQuote = quoteMessages[quoteMessages.length - 1];
                return latestQuote ? latestQuote.quoteNotes : viewingEstimate.quoteNotes || '';
              })(),
              service: viewingEstimate.service || '',
              userName: viewingEstimate.userName || '',
              userEmail: viewingEstimate.userEmail || '',
              phone: viewingEstimate.phone || '',
              address: viewingEstimate.address || viewingEstimate.location || ''
            }
          };
        }
        return msg;
      });

      // Add completion message
      updatedMessages.push(completionMessage);

      // Clean messages array to remove undefined values from nested objects
      const cleanMessages = updatedMessages.map(msg => {
        const cleanMsg = { ...msg };
        
        // Clean estimateData if it exists
        if (cleanMsg.estimateData) {
          Object.keys(cleanMsg.estimateData).forEach(key => {
            if (cleanMsg.estimateData[key] === undefined) {
              delete cleanMsg.estimateData[key];
            }
          });
        }
        
        // Remove any undefined top-level properties
        Object.keys(cleanMsg).forEach(key => {
          if (cleanMsg[key] === undefined) {
            delete cleanMsg[key];
          }
        });
        
        return cleanMsg;
      });

      // Clean data to remove undefined values
      const cleanUpdateData = {
        messages: cleanMessages,
        signatureStatus: 'completed',
        status: 'approved',
        signedAt: new Date(),
        signatureData: signatureData,
        updatedAt: new Date(),
        hasUnreadMessages: true // Flag for admin to see signature completion
      };

      // Remove any undefined values
      Object.keys(cleanUpdateData).forEach(key => {
        if (cleanUpdateData[key] === undefined) {
          delete cleanUpdateData[key];
        }
      });

      // Update estimate in Firestore with signature completion
      console.log('Updating Firestore with signature completion data:', cleanUpdateData);
      const estimateRef = doc(db, 'estimates', viewingEstimate.id);
      await updateDoc(estimateRef, cleanUpdateData);
      console.log('Firestore update completed successfully');

      // Notify admins about signature completion
      try {
        const adminUserIds = await getAdminUserIds();
        for (const adminId of adminUserIds) {
          await createEstimateNotification(viewingEstimate, 'signature_completed', adminId);
        }
      } catch (notificationError) {
        console.error('Error sending signature completion notifications to admins:', notificationError);
      }

      console.log('Signature submission completed successfully');
      showNotification('Document signed successfully! Your estimate has been approved.', 'success');

      // Refresh estimates
      console.log('Refreshing estimates...');
      loadEstimates();
      console.log('Estimates refreshed');

    } catch (error) {
      console.error('Error submitting signature:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      showNotification('Failed to submit signature. Please try again.', 'error');
    } finally {
      console.log('Setting submittingSignature to false');
      setSubmittingSignature(false);
    }
  };

  // Close modals
  const closeModals = () => {
    setShowViewModal(false);
    setShowEditModal(false);
    setViewingEstimate(null);
    setEditingEstimate(null);
  };

  return (
    <Layout>
      <div className="estimates-page" style={{
        padding: window.innerWidth <= 640 ? '0.5rem' : window.innerWidth <= 768 ? '1rem' : '1.5rem',
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}>
        {/* Toast Notification */}
        {notification && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 1000,
            maxWidth: '400px',
            padding: '1rem 1.5rem',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            backgroundColor: notification.type === 'success' ? '#4caf50' : '#f44336',
            color: 'white',
            fontSize: '0.875rem',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            animation: 'slideInRight 0.3s ease-out'
          }}>
            <span style={{ fontSize: '1.2rem' }}>
              {notification.type === 'success' ? '✅' : '❌'}
            </span>
            <span style={{ flex: 1 }}>{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                fontSize: '1.2rem',
                padding: '0',
                marginLeft: '0.5rem'
              }}
            >
              ✕
            </button>
          </div>
        )}

        <style>
          {`
            @keyframes slideInRight {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }


          `}
        </style>
        <div className="page-header">
          <div className="page-header-content">
            <div className="page-title-section">
              <h1 className="page-title">
                My Estimates
              </h1>
              <p className="page-subtitle">Manage your tree service estimate requests</p>
            </div>
            
            {/* Chat Button */}
          
            <button 
              className={`btn ${showNewEstimate ? 'btn-secondary' : 'btn-primary'} btn-lg`}
              onClick={() => setShowNewEstimate(!showNewEstimate)}
            >
              {showNewEstimate ? (
                <>
                  <span>✕</span>
                  Cancel
                </>
              ) : (
                <>
                  <img 
                    src="/estimate.png" 
                    alt="Request Estimate" 
                    style={{ 
                      width: '32px', 
                      height: '32px', 
                      objectFit: 'contain',
                      marginRight: '8px'
                    }} 
                  />
                  Request Estimate
                </>
              )}
            </button>
          </div>
        </div>

        {/* New Estimate Request Form */}
        {showNewEstimate && (
          <div className="modern-modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <div className="modern-modal" style={{
              background: 'white',
              borderRadius: '16px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              maxWidth: '700px',
              width: '100%',
              maxHeight: '95vh',
              overflow: 'hidden',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div className="modern-modal-header" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1rem 1.5rem',
                borderBottom: '1px solid #e5e7eb',
                flexShrink: 0
              }}>
                <h2 style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: 'var(--gray-900)',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <span className="modern-modal-icon">📋</span>
                  Request New Estimate
                </h2>
                <button 
                  className="modern-modal-close"
                  onClick={() => {
                    setShowNewEstimate(false);
                    setErrors({});
                    setSelectedService('');
                    setDescription('');
                    setFiles([]);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: 'var(--gray-500)',
                    padding: '0.5rem',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  ✕
                </button>
              </div>
              
              <div className="modern-modal-body" style={{ 
                padding: '1rem 1.5rem',
                flex: 1,
                overflow: 'auto',
                minHeight: 0
              }}>
            
            <form onSubmit={handleSubmitEstimate}>
              {/* Service Selection with Visual Cards */}
              <div className="modern-form-group" style={{ marginBottom: '1rem' }}>
                <label className="modern-form-label" style={{ 
                  fontSize: '1rem', 
                  fontWeight: '600', 
                  color: 'var(--gray-900)', 
                  marginBottom: '0.75rem' 
                }}>
                  Choose Your Service *
                </label>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
                  gap: '0.75rem' 
                }}>
                  {services.map((service) => {
                    const colors = getAppointmentColors(service.name);
                    const isSelected = selectedService === service.name;
                    return (
                      <div
                        key={service.name}
                        onClick={() => {
                          setSelectedService(service.name);
                          if (errors.service) {
                            setErrors(prev => ({ ...prev, service: '' }));
                          }
                        }}
                        style={{
                          padding: '0.75rem',
                          border: isSelected ? `2px solid ${colors.primary}` : '2px solid #e5e7eb',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          backgroundColor: isSelected ? colors.secondary : '#ffffff',
                          transition: 'all 0.3s ease',
                          transform: isSelected ? 'translateY(-2px)' : 'translateY(0)',
                          boxShadow: isSelected ? `0 8px 25px ${colors.primary}30` : '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                      >
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.75rem',
                          marginBottom: '0.5rem'
                        }}>
                          <div style={{
                            backgroundColor: colors.primary,
                            color: 'white',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '16px',
                            fontWeight: 'bold'
                          }}>
                            {colors.icon}
                          </div>
                          <div style={{ 
                            fontWeight: '600', 
                            color: isSelected ? colors.primary : 'var(--gray-900)',
                            fontSize: '0.9rem'
                          }}>
                            {service.name}
                          </div>
                        </div>
                        <div style={{ 
                          fontSize: '0.8rem', 
                          color: isSelected ? colors.accent : 'var(--gray-600)',
                          lineHeight: '1.4'
                        }}>
                          {service.description}
                        </div>
                        {isSelected && (
                          <div style={{
                            position: 'absolute',
                            top: '0.75rem',
                            right: '0.75rem',
                            backgroundColor: colors.primary,
                            color: 'white',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            ✓
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {errors.service && (
                  <div style={{ 
                    color: '#f44336', 
                    fontSize: '0.75rem', 
                    marginTop: '0.5rem' 
                  }}>
                    {errors.service}
                  </div>
                )}
              </div>

              {/* Service Details Section */}
              {selectedService && (
                <div style={{ 
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  backgroundColor: getAppointmentColors(selectedService).secondary,
                  borderRadius: '12px',
                  border: `1px solid ${getAppointmentColors(selectedService).primary}40`
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.75rem',
                    marginBottom: '0.75rem'
                  }}>
                    <div style={{
                      backgroundColor: getAppointmentColors(selectedService).primary,
                      color: 'white',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}>
                      {getAppointmentColors(selectedService).icon}
                    </div>
                    <div>
                      <div style={{ 
                        fontWeight: '600', 
                        color: getAppointmentColors(selectedService).primary,
                        fontSize: '1rem'
                      }}>
                        {selectedService}
                      </div>
                      <div style={{ 
                        fontSize: '0.8rem', 
                        color: getAppointmentColors(selectedService).accent
                      }}>
                        {services.find(s => s.name === selectedService)?.description}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="modern-form-group" style={{ marginBottom: '1rem' }}>
                <label className="modern-form-label" style={{ 
                  fontSize: '1rem', 
                  fontWeight: '600', 
                  color: 'var(--gray-900)', 
                  marginBottom: '0.75rem' 
                }}>
                  Project Description *
                  <span style={{ 
                    fontSize: '0.8rem', 
                    color: 'var(--gray-500)', 
                    fontWeight: 'normal',
                    display: 'block',
                    marginTop: '0.25rem'
                  }}>
                    Tell us about your specific needs and requirements ({description.length}/500 characters)
                  </span>
                </label>
                <textarea
                  className={`modern-form-input ${errors.description ? 'error' : ''}`}
                  rows="3"
                  maxLength="500"
                  placeholder="Describe your tree service needs in detail... (minimum 10 characters)"
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    if (errors.description) {
                      setErrors(prev => ({ ...prev, description: '' }));
                    }
                  }}
                  required
                />
                {errors.description && (
                  <div style={{ 
                    color: '#f44336', 
                    fontSize: '0.75rem', 
                    marginTop: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}>
                    <span>⚠️</span>
                    {errors.description}
                  </div>
                )}
                <small className="field-hint">
                  Be as specific as possible to help us provide an accurate estimate
                </small>
              </div>

              <div className="modern-form-group">
                <label className="modern-form-label">
                  Photos/Videos (Optional)
                  {files.length > 0 && (
                    <span style={{ 
                      fontSize: '0.75rem', 
                      color: '#2e7d32', 
                      fontWeight: 'normal',
                      marginLeft: '0.5rem'
                    }}>
                      - {files.length}/5 files selected
                    </span>
                  )}
                </label>
                <div style={{
                  border: '2px dashed #ddd',
                  borderRadius: '8px',
                  padding: '1rem',
                  textAlign: 'center',
                  backgroundColor: '#fafafa',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.style.borderColor = '#2e7d32';
                  e.currentTarget.style.backgroundColor = '#f1f8e9';
                }}
                onDragLeave={(e) => {
                  e.currentTarget.style.borderColor = '#ddd';
                  e.currentTarget.style.backgroundColor = '#fafafa';
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.style.borderColor = '#ddd';
                  e.currentTarget.style.backgroundColor = '#fafafa';
                  const droppedFiles = Array.from(e.dataTransfer.files);
                  handleFileChange({ target: { files: droppedFiles } });
                }}
                >
                <input
                  type="file"
                  className="modern-form-input"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleFileChange}
                    style={{ display: 'none' }}
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" style={{ cursor: 'pointer', display: 'block' }}>
                    <div style={{ fontSize: window.innerWidth <= 640 ? '1.5rem' : '2rem', marginBottom: '0.5rem', color: '#2e7d32' }}>
                      📁
                    </div>
                    <div style={{ fontSize: window.innerWidth <= 640 ? '0.875rem' : '1rem', fontWeight: '600', color: '#333', marginBottom: '0.5rem' }}>
                      Click to upload or drag and drop
                    </div>
                    <div style={{ fontSize: window.innerWidth <= 640 ? '0.75rem' : '0.875rem', color: '#666' }}>
                      Images and videos up to 10MB each (max 5 files)
                    </div>
                  </label>
                </div>
                <small className="field-hint">
                  📷 Photos and videos help us provide more accurate estimates
                </small>
                
                {/* File Previews */}
                {files.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      marginBottom: '1rem'
                    }}>
                      <strong style={{ color: '#2e7d32' }}>
                        📎 {files.length} file{files.length > 1 ? 's' : ''} selected
                      </strong>
                      <button
                        type="button"
                        onClick={() => setFiles([])}
                        style={{
                          background: 'none',
                          border: '1px solid #f44336',
                          color: '#f44336',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >
                        Clear All
                      </button>
                    </div>
                    
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                      gap: '1rem',
                      maxHeight: '400px',
                      overflowY: 'auto',
                      padding: '0.5rem',
                      border: '1px solid #eee',
                      borderRadius: '8px',
                      backgroundColor: '#fafafa'
                    }}>
                      {files.map((file, index) => (
                        <div key={index} style={{ 
                          position: 'relative',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          backgroundColor: 'white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          {/* Remove button */}
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            style={{
                              position: 'absolute',
                              top: '0.5rem',
                              right: '0.5rem',
                              background: 'rgba(244, 67, 54, 0.9)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '24px',
                              height: '24px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 10
                            }}
                            title="Remove file"
                          >
                            ✕
                          </button>

                          {/* Image Preview */}
                          {isImageFile(file) && (
                            <div>
                              <img
                                src={getFilePreviewUrl(file)}
                                alt={file.name}
                                style={{
                                  width: '100%',
                                  height: '150px',
                                  objectFit: 'cover',
                                  display: 'block'
                                }}
                                onLoad={(e) => {
                                  // Clean up object URL after image loads
                                  setTimeout(() => {
                                    URL.revokeObjectURL(e.target.src);
                                  }, 1000);
                                }}
                              />
                              <div style={{ 
                                padding: '0.5rem',
                                borderTop: '1px solid #eee'
                              }}>
                                <div style={{ 
                                  fontSize: '0.75rem', 
                                  fontWeight: '600',
                                  color: '#333',
                                  marginBottom: '0.25rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  📷 {file.name}
                                </div>
                                <div style={{ 
                                  fontSize: '0.7rem', 
                                  color: '#666'
                                }}>
                                  {(file.size / 1024 / 1024).toFixed(2)} MB
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Video Preview */}
                          {isVideoFile(file) && (
                            <div>
                              <video
                                src={getFilePreviewUrl(file)}
                                controls
                                style={{
                                  width: '100%',
                                  height: '150px',
                                  objectFit: 'cover',
                                  display: 'block'
                                }}
                                onLoadedData={(e) => {
                                  // Clean up object URL after video loads
                                  setTimeout(() => {
                                    URL.revokeObjectURL(e.target.src);
                                  }, 1000);
                                }}
                              />
                              <div style={{ 
                                padding: '0.5rem',
                                borderTop: '1px solid #eee'
                              }}>
                                <div style={{ 
                                  fontSize: '0.75rem', 
                                  fontWeight: '600',
                                  color: '#333',
                                  marginBottom: '0.25rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  🎥 {file.name}
                                </div>
                                <div style={{ 
                                  fontSize: '0.7rem', 
                                  color: '#666'
                                }}>
                                  {(file.size / 1024 / 1024).toFixed(2)} MB
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Other file types */}
                          {!isImageFile(file) && !isVideoFile(file) && (
                            <div style={{ 
                              padding: '1rem',
                              textAlign: 'center',
                              height: '150px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              backgroundColor: '#f5f5f5'
                            }}>
                              <div style={{ 
                                fontSize: window.innerWidth <= 640 ? '1.5rem' : '2rem', 
                                marginBottom: '0.5rem',
                                color: '#666'
                              }}>
                                📄
                              </div>
                              <div style={{ 
                                fontSize: '0.75rem', 
                                fontWeight: '600',
                                color: '#333',
                                marginBottom: '0.25rem',
                                textAlign: 'center',
                                wordBreak: 'break-word'
                              }}>
                                {file.name}
                              </div>
                              <div style={{ 
                                fontSize: '0.7rem', 
                                color: '#666'
                              }}>
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '1rem', 
                justifyContent: 'flex-end',
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: '1px solid #eee',
                flexShrink: 0
              }}>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowNewEstimate(false);
                    setErrors({});
                    setSelectedService('');
                    setDescription('');
                    setFiles([]);
                  }}
                  disabled={loading}
                  style={{
                    opacity: loading ? 0.6 : 1,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    backgroundColor: '#f3f4f6',
                    borderColor: '#d1d5db',
                    color: '#374151',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={loading}
                  style={{
                    minWidth: '160px',
                    position: 'relative',
                    opacity: loading ? 0.8 : 1,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    backgroundColor: selectedService ? getAppointmentColors(selectedService).primary : '#6b7280',
                    borderColor: selectedService ? getAppointmentColors(selectedService).primary : '#6b7280',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    boxShadow: selectedService ? `0 4px 12px ${getAppointmentColors(selectedService).primary}40` : 'none',
                    transform: selectedService ? 'translateY(-1px)' : 'translateY(0)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {loading ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                      <span style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid transparent',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></span>
                      Submitting...
                    </span>
                  ) : (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                      <img 
                        src="/estimate.png" 
                        alt="Submit Request" 
                        style={{ 
                          width: '20px', 
                          height: '20px', 
                          objectFit: 'contain',
                          marginRight: '8px'
                        }} 
                      />
                      Submit Request
                    </span>
                  )}
                </button>
              </div>

              <style>
                {`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                  
                  .modern-form-input.error {
                    border-color: #f44336;
                    box-shadow: 0 0 0 2px rgba(244, 67, 54, 0.1);
                  }
                  
                  .modern-form-input:focus {
                    border-color: #2e7d32;
                    box-shadow: 0 0 0 2px rgba(46, 125, 50, 0.1);
                    outline: none;
                  }
                `}
              </style>
            </form>
              </div>
            </div>
          </div>
        )}
        
        {/* Existing Estimates */}
        <div className="modern-card">
          <div className="modern-card-header">
            <h3 className="modern-card-title">
              <img 
                src="/estimate.png" 
                alt="Your Estimate Requests" 
                className="modern-card-icon"
                style={{ 
                  width: '24px', 
                  height: '24px', 
                  objectFit: 'contain'
                }} 
              />
              Your Estimate Requests
            </h3>
            <p className="modern-card-subtitle">
              {estimates.length > 0 ? `${estimates.length} estimate${estimates.length > 1 ? 's' : ''} found` : 'No estimates yet'}
            </p>
          </div>
          
          {loadingEstimates ? (
            <div className="modern-loading-state">
              <div className="modern-loading-spinner"></div>
              <h4>Loading your estimates...</h4>
              <p>Please wait while we fetch your data</p>
            </div>
          ) : estimates.length > 0 ? (
            <div className="modern-grid">
              {estimates.map((estimate) => (
                <div key={estimate.id} className="modern-estimate-card animate-scale">
                  <div className="modern-estimate-header">
                    <div className="modern-estimate-service">
                      <div className="modern-estimate-icon">
                        {estimate.service.includes('Tree Removal') ? '◆' :
                         estimate.service.includes('Pruning') ? '◇' :
                         estimate.service.includes('Stump') ? '◈' :
                         estimate.service.includes('Emergency') ? '◉' : '◌'}
                      </div>
                      <div>
                        <h4 className="modern-estimate-title">{estimate.service}</h4>
                        <p className="modern-estimate-date">Submitted {estimate.submittedDate}</p>
                      </div>
                    </div>
                    
                    <div className="modern-estimate-status">
                      <span className={`modern-status-badge ${
                        estimate.status === 'pending' ? 'status-warning' :
                        estimate.status === 'quoted' ? 'status-success' :
                        estimate.status === 'approved' ? 'status-info' :
                        estimate.status === 'declined' ? 'status-error' :
                        'status-pending'
                      }`}>
                        {estimate.status}
                      </span>
                      
                      {estimate.signatureStatus && (
                        <span className={`modern-status-badge ${
                          estimate.signatureStatus === 'completed' ? 'status-success' :
                          estimate.signatureStatus === 'pending' ? 'status-warning' :
                          'status-pending'
                        }`} style={{ fontSize: '0.7rem', marginTop: '0.25rem' }}>
                          {estimate.signatureStatus === 'completed' ? '✅ Signed' :
                           estimate.signatureStatus === 'pending' ? '⏳ Sign Pending' :
                           '📝 Ready to Sign'}
                        </span>
                      )}
                      
                      {estimate.messages && estimate.messages.some(msg => msg.sender === 'admin' && !msg.read) && (
                        <span className="modern-new-message-badge">
                          New Message
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="modern-estimate-content">
                    <p className="modern-estimate-description">{estimate.description}</p>
                    
                    <div className="modern-estimate-meta">
                      {estimate.files && estimate.files.length > 0 && (
                        <span className="modern-meta-item">
                          📎 {estimate.files.length} file{estimate.files.length > 1 ? 's' : ''}
                        </span>
                      )}
                      {estimate.messages && estimate.messages.length > 0 && (
                        <span className="modern-meta-item">
                          ✉ {estimate.messages.length} message{estimate.messages.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="modern-estimate-actions">
                    <button 
                      className="btn btn-primary btn-sm"
                      onClick={() => handleViewEstimate(estimate)}
                    >
                      <span>◐</span>
                      View Details
                    </button>
                    
                    {estimate.signatureStatus === 'pending' && estimate.signaturePdfUrl && (
                      <button 
                        className="btn btn-success btn-sm"
                        onClick={() => window.open(estimate.signaturePdfUrl, '_blank')}
                      >
                        <span>✍️</span>
                        Sign Document
                      </button>
                    )}
                    
                    {canEditOrDelete(estimate.status) && (
                      <>
                        <button 
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleEditEstimate(estimate)}
                        >
                          <span>✏️</span>
                          Edit
                        </button>
                        <button 
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteEstimate(estimate.id)}
                        >
                          <span>🗑️</span>
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="modern-empty-state">
              <div className="modern-empty-icon">📋</div>
              <h4 className="modern-empty-title">No estimate requests yet</h4>
              <p className="modern-empty-description">
                Get started by requesting your first tree service estimate. 
                Our team will review your request and provide a detailed quote.
              </p>
              <button 
                className="btn btn-primary btn-lg"
                onClick={() => setShowNewEstimate(true)}
              >
                <span>📋</span>
                Request Your First Estimate
              </button>
            </div>
          )}
        </div>

        {/* View Estimate Modal */}
        {showViewModal && viewingEstimate && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }} onClick={closeModals}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '900px',
              width: '95%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              position: 'relative'
            }} onClick={(e) => e.stopPropagation()}>
              {/* Modal Header */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '2rem',
                borderBottom: '1px solid #e5e7eb',
                paddingBottom: '1.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.25rem'
                  }}>
                    📋
                  </div>
                  <h2 style={{ 
                    margin: 0, 
                    color: '#2e7d32', 
                    fontSize: window.innerWidth <= 640 ? '1.25rem' : '1.5rem',
                    fontWeight: '600'
                  }}>
                    Estimate Details
                  </h2>
                </div>
                <button 
                  onClick={closeModals}
                  style={{
                    background: '#f3f4f6',
                    border: 'none',
                    fontSize: '1.25rem',
                    cursor: 'pointer',
                    color: '#6b7280',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '40px',
                    height: '40px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#e5e7eb';
                    e.target.style.color = '#374151';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f3f4f6';
                    e.target.style.color = '#6b7280';
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Modal Content */}
              <div style={{ display: 'grid', gap: '2rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
                  <div>
                    <h3 style={{ margin: '0 0 0.75rem 0', color: '#374151', fontSize: '1rem', fontWeight: '600' }}>Service Type</h3>
                    <div style={{ 
                      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', 
                      padding: '1.25rem', 
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}>
                      <p style={{ margin: 0, fontWeight: '600', fontSize: '1.125rem', color: '#1e293b' }}>
                        {viewingEstimate.service}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 style={{ margin: '0 0 0.75rem 0', color: '#374151', fontSize: '1rem', fontWeight: '600' }}>Status</h3>
                    <div style={{ 
                      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', 
                      padding: '1.25rem', 
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}>
                      <span style={{
                        background: getStatusColor(viewingEstimate.status),
                        color: 'white',
                        padding: '0.75rem 1.25rem',
                        borderRadius: '25px',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.025em',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}>
                        {viewingEstimate.status}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1rem' }}>Description</h3>
                  <div style={{ background: '#f9f9f9', padding: '1rem', borderRadius: '6px' }}>
                    <p style={{ margin: 0, lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                      {viewingEstimate.description}
                    </p>
                  </div>
                </div>

                  <div>
                    <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1rem' }}>
                    📎 Attached Files {viewingEstimate.files && viewingEstimate.files.length > 0 ? `(${viewingEstimate.files.length})` : '(0)'}
                    </h3>
                  {viewingEstimate.files && viewingEstimate.files.length > 0 ? (
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                      gap: '1rem',
                      background: '#f9f9f9', 
                      padding: '1rem', 
                      borderRadius: '6px' 
                    }}>
                      {viewingEstimate.files.map((file, index) => (
                        <div key={index} style={{
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          backgroundColor: 'white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          cursor: 'pointer',
                          transition: 'transform 0.2s ease'
                        }}
                        onClick={() => window.open(file.url, '_blank')}
                        onMouseOver={(e) => {
                          e.currentTarget.style.transform = 'scale(1.02)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                        >
                          {/* Image Preview */}
                          {file.type && file.type.startsWith('image/') ? (
                            <div>
                            <img
                              src={file.url}
                              alt={file.name}
                              style={{
                                width: '100%',
                                  height: '150px',
                                  objectFit: 'cover',
                                  display: 'block'
                                }}
                              />
                              <div style={{ 
                                padding: '0.75rem',
                                borderTop: '1px solid #eee'
                              }}>
                                <div style={{ 
                                  fontSize: '0.8rem', 
                                  fontWeight: '600',
                                  color: '#333',
                                  marginBottom: '0.25rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  📷 {file.name}
                                </div>
                                <div style={{ 
                                  fontSize: '0.7rem', 
                                  color: '#666'
                                }}>
                                  {file.size ? (file.size / 1024 / 1024).toFixed(2) + ' MB' : 'Unknown size'}
                                </div>
                              </div>
                            </div>
                          ) : file.type && file.type.startsWith('video/') ? (
                            <div>
                              <video
                                src={file.url}
                                controls
                                style={{
                                  width: '100%',
                                  height: '150px',
                                  objectFit: 'cover',
                                  display: 'block'
                                }}
                                onClick={(e) => e.stopPropagation()} // Prevent opening in new tab when clicking video controls
                              />
                              <div style={{ 
                                padding: '0.75rem',
                                borderTop: '1px solid #eee'
                              }}>
                                <div style={{ 
                                  fontSize: '0.8rem', 
                                  fontWeight: '600',
                                  color: '#333',
                                  marginBottom: '0.25rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  🎥 {file.name}
                                </div>
                                <div style={{ 
                                  fontSize: '0.7rem', 
                                  color: '#666'
                                }}>
                                  {file.size ? (file.size / 1024 / 1024).toFixed(2) + ' MB' : 'Unknown size'}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div style={{
                              padding: '1rem',
                              textAlign: 'center',
                              height: '150px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              backgroundColor: '#f5f5f5'
                            }}>
                              <div style={{ 
                                fontSize: window.innerWidth <= 640 ? '1.75rem' : '2.5rem', 
                                marginBottom: '0.5rem',
                                color: '#666'
                            }}>
                              📄
                            </div>
                              <div style={{ 
                                fontSize: '0.8rem', 
                                fontWeight: '600',
                                color: '#333',
                                marginBottom: '0.25rem',
                                textAlign: 'center',
                                wordBreak: 'break-word'
                              }}>
                              {file.name}
                            </div>
                              <div style={{ 
                                fontSize: '0.7rem', 
                                color: '#666'
                              }}>
                              {file.size ? (file.size / 1024 / 1024).toFixed(2) + ' MB' : 'Unknown size'}
                            </div>
                              <div style={{ 
                                fontSize: '0.7rem', 
                                color: '#2e7d32',
                                marginTop: '0.25rem',
                                fontWeight: '600'
                              }}>
                                Click to open
                          </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      background: '#f9f9f9',
                      padding: '2rem',
                      borderRadius: '6px',
                      textAlign: 'center',
                      color: '#666',
                      fontSize: '0.875rem'
                    }}>
                      <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📁</div>
                      <p style={{ margin: '0 0 0.5rem 0' }}>No files attached to this estimate</p>
                      <p style={{ margin: 0, fontSize: '0.75rem', color: '#999' }}>
                        Files can be added when editing the estimate
                      </p>
                  </div>
                )}
                </div>

                <div>
                  <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1rem' }}>Timestamps</h3>
                  <div style={{ background: '#f9f9f9', padding: '1rem', borderRadius: '6px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.875rem' }}>
                      <div>
                        <p style={{ margin: '0', color: '#666' }}>Submitted</p>
                        <p style={{ margin: '0', fontWeight: '600' }}>
                          {viewingEstimate.submittedDate}
                        </p>
                      </div>
                      <div>
                        <p style={{ margin: '0', color: '#666' }}>Last Updated</p>
                        <p style={{ margin: '0', fontWeight: '600' }}>
                          {viewingEstimate.updatedAt ? 
                            new Date(viewingEstimate.updatedAt.seconds * 1000).toLocaleDateString() : 
                            viewingEstimate.submittedDate
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 style={{ margin: '1.5rem 0 0.5rem 0', color: '#333', fontSize: '1rem' }}>💬 Messages History</h3>
                  <div style={{
                    background: 'white',
                    borderRadius: '12px',
                    border: '1px solid #e0e0e0',
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '400px'
                  }}>
                    <div style={{
                      padding: '0.75rem 1rem',
                      borderBottom: '1px solid #e0e0e0',
                      background: '#f8f9fa'
                    }}>
                      <div style={{ fontSize: '0.9rem', color: '#666', fontWeight: 500 }}>
                        📋 Client: {viewingEstimate.userName || viewingEstimate.userEmail?.split('@')[0] || 'Client'}
                      </div>
                      {viewingEstimate.messages && viewingEstimate.messages.length > 0 && (
                        <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.25rem' }}>
                          {viewingEstimate.messages.length} message{viewingEstimate.messages.length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>

                    <div 
                      id="client-messages-container"
                      style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '1rem',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem'
                      }}>
                      {viewingEstimate.messages && viewingEstimate.messages.length > 0 ? (
                        viewingEstimate.messages.map((message, index) => {
                          const isAdmin = message.sender === 'admin';
                          const isClient = message.sender === 'client';
                          
                          return (
                            <div
                              key={index}
                              style={{
                                display: 'flex',
                                gap: '0.75rem',
                                alignItems: 'flex-start'
                              }}
                            >
                              {/* Avatar/Icon */}
                              <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                background: isAdmin ? 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)' : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1.1rem',
                                flexShrink: 0,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                              }}>
                                {isAdmin ? '👨‍💼' : isClient ? '👤' : '🤖'}
                              </div>
                              
                              {/* Message Bubble */}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                  background: isAdmin ? '#e8f5e9' : '#f3f4f6',
                                  padding: '0.75rem',
                                  borderRadius: '12px',
                                  borderTopLeftRadius: '4px',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                  border: `1px solid ${isAdmin ? '#c8e6c9' : '#e5e7eb'}`
                                }}>
                                  <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '0.4rem'
                                  }}>
                                    <strong style={{
                                      color: isAdmin ? '#2e7d32' : '#4b5563',
                                      fontSize: '0.85rem',
                                      fontWeight: '600'
                                    }}>
                                      {isAdmin ? 'Admin' : isClient ? 'You' : 'System'}
                                    </strong>
                                    {message.timestamp && (
                                      <span style={{
                                        fontSize: '0.7rem',
                                        color: '#9ca3af'
                                      }}>
                                        {new Date(message.timestamp).toLocaleString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ 
                                    color: '#1f2937', 
                                    lineHeight: '1.5',
                                    fontSize: '0.9rem',
                                    wordWrap: 'break-word'
                                  }}>
                                    {message.isQuote ? (
                                      <div>
                                        <div style={{ fontWeight: '600', color: '#16a34a', marginBottom: '0.25rem' }}>
                                          💰 Quote: ${message.quoteAmount?.toLocaleString()}
                                        </div>
                                        {message.quoteNotes && (
                                          <div style={{ fontSize: '0.85rem', color: '#6b7280', fontStyle: 'italic' }}>
                                            {message.quoteNotes}
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      message.text || 'File shared'
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div style={{ textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
                          No messages yet
                        </div>
                      )}
                    </div>

                    {/* Message Input Area */}
                    <div style={{
                      borderTop: '1px solid #e0e0e0',
                      padding: '1rem',
                      background: '#ffffff'
                    }}>
                      <textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message here..."
                        style={{
                          width: '100%',
                          minHeight: '80px',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '0.9rem',
                          resize: 'vertical',
                          fontFamily: 'inherit',
                          marginBottom: '0.75rem'
                        }}
                        disabled={sendingMessage}
                      />
                      <button
                        onClick={async () => {
                          if (!newMessage.trim() || !viewingEstimate || sendingMessage) return;
                          
                          setSendingMessage(true);
                          try {
                            const message = {
                              id: Date.now().toString(),
                              text: newMessage.trim(),
                              sender: 'client',
                              senderName: currentUser.displayName || currentUser.email.split('@')[0],
                              senderEmail: currentUser.email,
                              timestamp: new Date(),
                              read: false
                            };
                            
                            const currentMessages = viewingEstimate.messages || [];
                            const updatedMessages = [...currentMessages, message];
                            
                            await updateDoc(doc(db, 'estimates', viewingEstimate.id), {
                              messages: updatedMessages,
                              updatedAt: new Date(),
                              hasUnreadMessages: true
                            });
                            
                            // Update local state
                            setViewingEstimate({
                              ...viewingEstimate,
                              messages: updatedMessages
                            });
                            
                            setNewMessage('');
                            showNotification('Message sent successfully!', 'success');
                            
                          } catch (error) {
                            console.error('Error sending message:', error);
                            showNotification('Failed to send message. Please try again.', 'error');
                          } finally {
                            setSendingMessage(false);
                          }
                        }}
                        disabled={sendingMessage || !newMessage.trim()}
                        style={{
                          background: sendingMessage || !newMessage.trim() ? '#9ca3af' : '#2e7d32',
                          color: 'white',
                          border: 'none',
                          padding: '0.75rem 1.5rem',
                          borderRadius: '8px',
                          cursor: sendingMessage || !newMessage.trim() ? 'not-allowed' : 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          transition: 'background 0.2s'
                        }}
                      >
                        {sendingMessage ? (
                          <>
                            <div style={{
                              width: '14px',
                              height: '14px',
                              border: '2px solid transparent',
                              borderTop: '2px solid white',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite'
                            }}></div>
                            Sending...
                          </>
                        ) : (
                          'Send Message'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Estimate Modal */}
        {showEditModal && editingEstimate && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '2rem',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
            }}>
              {/* Modal Header */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '1.5rem',
                borderBottom: '2px solid #eee',
                paddingBottom: '1rem'
              }}>
                <h2 style={{ margin: 0, color: '#2e7d32', fontSize: '1.5rem' }}>
                  Edit Estimate
                </h2>
                <button 
                  onClick={closeModals}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '0.5rem'
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Edit Form */}
              <form onSubmit={handleUpdateEstimate}>
                <div className="modern-form-group">
                  <label className="modern-form-label">Service Type *</label>
                  <select
                    className={`modern-form-input ${errors.service ? 'error' : ''}`}
                    value={selectedService}
                    onChange={(e) => {
                      setSelectedService(e.target.value);
                      if (errors.service) {
                        setErrors(prev => ({ ...prev, service: '' }));
                      }
                    }}
                    required
                  >
                    <option value="">Select a service</option>
                    {services.map((service) => (
                      <option key={service.name} value={service.name}>
                        {service.icon} {service.name}
                      </option>
                    ))}
                  </select>
                  {errors.service && (
                    <div style={{ 
                      color: '#f44336', 
                      fontSize: '0.75rem', 
                      marginTop: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      <span>⚠️</span>
                      {errors.service}
                    </div>
                  )}
                </div>

                <div className="modern-form-group">
                  <label className="modern-form-label">
                    Description * 
                    <span style={{ 
                      fontSize: '0.75rem', 
                      color: '#666', 
                      fontWeight: 'normal',
                      marginLeft: '0.5rem'
                    }}>
                      ({description.length}/500 characters)
                    </span>
                  </label>
                  <textarea
                    className={`modern-form-input ${errors.description ? 'error' : ''}`}
                    rows="4"
                    maxLength="500"
                    placeholder="Describe your tree service needs in detail... (minimum 10 characters)"
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      if (errors.description) {
                        setErrors(prev => ({ ...prev, description: '' }));
                      }
                    }}
                    required
                  />
                  {errors.description && (
                    <div style={{ 
                      color: '#f44336', 
                      fontSize: '0.75rem', 
                      marginTop: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      <span>⚠️</span>
                      {errors.description}
                    </div>
                  )}
                </div>

                {/* Existing Files Display */}
                  <div style={{ marginBottom: '1rem' }}>
                  <label className="modern-form-label">
                    Current Files {editingEstimate.files && editingEstimate.files.length > 0 ? `(${editingEstimate.files.length})` : '(0)'}
                  </label>
                  {editingEstimate.files && editingEstimate.files.length > 0 ? (
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                      gap: '1rem',
                      background: '#f9f9f9', 
                      padding: '1rem', 
                      borderRadius: '6px' 
                    }}>
                      {editingEstimate.files.map((file, index) => (
                        <div key={index} style={{
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          backgroundColor: 'white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          {/* Image Preview */}
                          {file.type && file.type.startsWith('image/') ? (
                            <div>
                              <img
                                src={file.url}
                                alt={file.name}
                                style={{
                                  width: '100%',
                                  height: '120px',
                                  objectFit: 'cover',
                                  display: 'block'
                                }}
                              />
                              <div style={{ 
                                padding: '0.5rem',
                                borderTop: '1px solid #eee'
                              }}>
                                <div style={{ 
                                  fontSize: '0.75rem', 
                                  fontWeight: '600',
                                  color: '#333',
                                  marginBottom: '0.25rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  📷 {file.name}
                                </div>
                                <div style={{ 
                                  fontSize: '0.7rem', 
                                  color: '#666'
                                }}>
                                  {file.size ? (file.size / 1024 / 1024).toFixed(2) + ' MB' : 'Unknown size'}
                                </div>
                              </div>
                            </div>
                          ) : file.type && file.type.startsWith('video/') ? (
                            <div>
                              <video
                                src={file.url}
                                controls
                                style={{
                                  width: '100%',
                                  height: '120px',
                                  objectFit: 'cover',
                                  display: 'block'
                                }}
                              />
                              <div style={{ 
                                padding: '0.5rem',
                                borderTop: '1px solid #eee'
                              }}>
                                <div style={{ 
                                  fontSize: '0.75rem', 
                                  fontWeight: '600',
                                  color: '#333',
                                  marginBottom: '0.25rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  🎥 {file.name}
                                </div>
                                <div style={{ 
                                  fontSize: '0.7rem', 
                                  color: '#666'
                                }}>
                                  {file.size ? (file.size / 1024 / 1024).toFixed(2) + ' MB' : 'Unknown size'}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div style={{ 
                              padding: '1rem',
                          textAlign: 'center',
                              height: '120px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              backgroundColor: '#f5f5f5'
                            }}>
                              <div style={{ 
                                fontSize: window.innerWidth <= 640 ? '1.5rem' : '2rem', 
                                marginBottom: '0.5rem',
                                color: '#666'
                              }}>
                                📄
                          </div>
                              <div style={{ 
                                fontSize: '0.75rem', 
                                fontWeight: '600',
                                color: '#333',
                                marginBottom: '0.25rem',
                                textAlign: 'center',
                                wordBreak: 'break-word'
                              }}>
                            {file.name}
                          </div>
                              <div style={{ 
                                fontSize: '0.7rem', 
                                color: '#666'
                              }}>
                                {file.size ? (file.size / 1024 / 1024).toFixed(2) + ' MB' : 'Unknown size'}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      background: '#f9f9f9',
                      padding: '2rem',
                      borderRadius: '6px',
                      textAlign: 'center',
                      color: '#666',
                      fontSize: '0.875rem'
                    }}>
                      <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📁</div>
                      <p style={{ margin: '0 0 0.5rem 0' }}>No files attached to this estimate</p>
                      <p style={{ margin: 0, fontSize: '0.75rem', color: '#999' }}>
                        You can add new files below
                      </p>
                  </div>
                )}
                </div>

                {/* Add New Files */}
                <div className="modern-form-group">
                  <label className="modern-form-label">Add New Files (Optional)</label>
                  <div style={{
                    border: '2px dashed #ddd',
                    borderRadius: '8px',
                    padding: '1rem',
                    textAlign: 'center',
                    backgroundColor: '#fafafa'
                  }}>
                    <input
                      type="file"
                      multiple
                      accept="image/*,video/*"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                      id="edit-file-upload"
                    />
                    <label htmlFor="edit-file-upload" style={{ cursor: 'pointer', display: 'block' }}>
                      <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem', color: '#2e7d32' }}>
                        📁
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#666' }}>
                        Click to add more files
                      </div>
                    </label>
                  </div>
                  
                  {/* New Files Preview */}
                  {files.length > 0 && (
                    <div style={{ marginTop: '1rem' }}>
                      <strong style={{ color: '#2e7d32' }}>
                        📎 {files.length} new file{files.length > 1 ? 's' : ''} to add
                      </strong>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', 
                        gap: '0.5rem',
                        marginTop: '0.5rem'
                      }}>
                        {files.map((file, index) => (
                          <div key={index} style={{
                            textAlign: 'center',
                            fontSize: '0.75rem',
                            position: 'relative'
                          }}>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              style={{
                                position: 'absolute',
                                top: '-5px',
                                right: '-5px',
                                background: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                fontSize: '0.7rem',
                                cursor: 'pointer'
                              }}
                            >
                              ✕
                            </button>
                            <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>
                              {isImageFile(file) ? '🖼️' : '📄'}
                            </div>
                            <div style={{ wordBreak: 'break-word' }}>
                              {file.name}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ 
                  display: 'flex', 
                  gap: '1rem', 
                  justifyContent: 'flex-end',
                  marginTop: '2rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid #eee'
                }}>
                  <button 
                    type="button" 
                    onClick={closeModals}
                    disabled={loading}
                    style={{
                      background: '#666',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '6px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      opacity: loading ? 0.6 : 1
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    disabled={loading}
                    style={{
                      background: '#2e7d32',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '6px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      minWidth: '140px',
                      opacity: loading ? 0.8 : 1
                    }}
                  >
                    {loading ? (
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                        <span style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid transparent',
                          borderTop: '2px solid white',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></span>
                        Updating...
                      </span>
                    ) : (
                      '✏️ Update Estimate'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && estimateToDelete && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              textAlign: 'center'
            }}>
              {/* Warning Icon */}
              <div style={{
                fontSize: '3rem',
                marginBottom: '1rem',
                color: '#f44336'
              }}>
                ⚠️
              </div>

              {/* Title */}
              <h2 style={{
                margin: '0 0 1rem 0',
                color: '#333',
                fontSize: '1.5rem',
                fontWeight: 'bold'
              }}>
                Delete Estimate Request
              </h2>

              {/* Message */}
              <p style={{
                margin: '0 0 1.5rem 0',
                color: '#666',
                fontSize: '1rem',
                lineHeight: '1.5'
              }}>
                Are you sure you want to delete this estimate request for <strong>{estimateToDelete.service}</strong>?
                <br />
                <span style={{ fontSize: '0.875rem', color: '#999' }}>
                  This action cannot be undone.
                </span>
              </p>

              {/* Buttons */}
              <div style={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'center',
                marginTop: '2rem'
              }}>
                <button
                  onClick={cancelDeleteEstimate}
                  disabled={deleting}
                  style={{
                    background: '#f5f5f5',
                    color: '#333',
                    border: '1px solid #ddd',
                    padding: '0.75rem 2rem',
                    borderRadius: '8px',
                    cursor: deleting ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    fontWeight: '600',
                    minWidth: '120px',
                    opacity: deleting ? 0.6 : 1,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    if (!deleting) {
                      e.target.style.backgroundColor = '#e9e9e9';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!deleting) {
                      e.target.style.backgroundColor = '#f5f5f5';
                    }
                  }}
                >
                  Cancel
                </button>
                
                <button
                  onClick={confirmDeleteEstimate}
                  disabled={deleting}
                  style={{
                    background: deleting ? '#ccc' : '#f44336',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 2rem',
                    borderRadius: '8px',
                    cursor: deleting ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    fontWeight: '600',
                    minWidth: '120px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    if (!deleting) {
                      e.target.style.backgroundColor = '#d32f2f';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!deleting) {
                      e.target.style.backgroundColor = '#f44336';
                    }
                  }}
                >
                  {deleting ? (
                    <>
                      <span style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid transparent',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></span>
                      Deleting...
                    </>
                  ) : (
                    <>
                      🗑️ Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Estimates;